import { createAsyncThunk, createEntityAdapter, createSlice, nanoid } from '@reduxjs/toolkit';
import { JSONApiDataObject, MediaAttachment, ReduxEntity, User } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { addLocalMessage, addRemoteMessage, generateMessagesEndpoint } from 'Douleutaras/redux/data/conversations/messagesSlice';
import normalize from 'json-api-normalizer';
import { PickerFileCallback } from 'filestack-react';
import { makeRequest } from 'Douleutaras/services/apiClient';

export const mediaAttachmentsAdapter = createEntityAdapter<JSONApiDataObject<MediaAttachment>>({
  selectId: attachment => attachment.id
});

const loadImage = async src =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (...args) => reject(args);
    img.src = src;
  });

const getImageData = image => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0);

  return context.getImageData(0, 0, image.width, image.height);
};

export const onFileSubmit = createAsyncThunk(
  'file/submit',
  async ({ resource, conversationId, user }
  : { resource: PickerFileCallback, conversationId: number, user: User },
         api) => {

    const attachments = [];
    const attachmentsRemote = [];
    const uniqueIds = [];

    // Create attachments for Local Message, for fast insertion
    for (const file of resource.filesUploaded) {
      const uniqueId = nanoid();
      uniqueIds.push(uniqueId); // create uniq id for each post
      const attachment = {
        handle: file.handle,
        s3Key: file.key,
        filename: file.filename,
        metaData: {
          size: file.size,
          mimetype: file.mimetype
        },
        cdnUrl: file.url
      } as MediaAttachment;

      attachments.push(attachment);
    }

    attachments.forEach((attachment, index) => {
      const uniqueId = uniqueIds[index];
      api.dispatch(addLocalMessage({
        text: undefined, // empty body
        attachments: [ attachment ],
        conversationId,
        senderId: user.id,
        uuid: uniqueId
      }));
    });

    // Create a separate list for remote upload (ie through API), including BLURHASH which is a heavy operation on images
    for await (const file of resource.filesUploaded) {
      const attachment = {
        handle: file.handle,
        s3Key: file.key,
        filename: file.filename,
        metaData: {
          size: file.size,
          mimetype: file.mimetype
        },
        cdnUrl: file.url
      } as MediaAttachment;

      /* Temporary disable */
      /*
      if (file.mimetype.match('image.*')) {
        // Loads the image into dom to extract its information
        const image = await loadImage(file.url); // Experimental. This may freeze user's browser
        // Extracts image info from DOM
        const imageData = getImageData(image);
        // https://blurha.sh/
        attachment.metaData.blurhash = encode(imageData.data, imageData.width, imageData.height, 4, 4);
        attachment.metaData.width = imageData.width;
        attachment.metaData.height = imageData.height;
      }
      */

      attachmentsRemote.push(attachment);
    }

    attachmentsRemote.forEach((attachment, index) => {
      const uniqueId = uniqueIds[index];
      api.dispatch(addRemoteMessage({
        text: undefined,
        attachments: [ attachment ],
        conversationId,
        uuid: uniqueId
      }));
    });
  }
);

export const generateMediaAttachmentsEndpoint = (conversationId: string | number) => {
  return `api/public/v3/conversation/${conversationId}/media-attachments/`;
};

export const fetchAttachments = createAsyncThunk(
  'mediaAttachments/getAll',
  async ({ conversationId }: { conversationId: string }, api) => {
    const endpoint = generateMediaAttachmentsEndpoint(conversationId);
    const response = await makeRequest(endpoint, {
      method: 'GET',
      additionalGetParams: {
        sort: 'created'
      }
    })(api);

    return normalize(response.data, { endpoint });
  }
);

export const localMediaAttachmentsAdded = createAsyncThunk(
  'mediaAttachments/Add',
  ({ conversationId, attachmentsEntity }: {
    conversationId: number,
    attachmentsEntity: Array<JSONApiDataObject<MediaAttachment>> },
   api) => {

    const metaEndpoint = generateMediaAttachmentsEndpoint(conversationId);

    const meta = attachmentsEntity.map(attachmentEntity => {
      return {
        id: attachmentEntity.id,
        type: attachmentEntity.type
      };
    });

    return { attachmentsEntity, metaEndpoint, meta };
  }
);

export const mediaAttachmentsSlice = createSlice({
  name: 'mediaAttachments',
  initialState: mediaAttachmentsAdapter.getInitialState({
    loading: undefined,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<MediaAttachment>>),
  reducers: {
    mediaAttachmentsAdded: (state, action) => {
      mediaAttachmentsAdapter.setMany(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAttachments.fulfilled, (state, action) => {
      mediaAttachmentsAdapter.upsertMany(state, action.payload.mediaAttachments || {});
      state.meta = { ...state.meta, ...action.payload.meta };
    });
    builder.addCase(localMediaAttachmentsAdded.fulfilled, (state, action) => {
      const endpoint = action.payload.metaEndpoint;
      mediaAttachmentsAdapter.setMany(state, action.payload.attachmentsEntity);
      if (state.meta[action.payload.metaEndpoint] === undefined) {
        state.meta[action.payload.metaEndpoint] = { data: [], meta: {} };
      }
      state.meta[endpoint].data = [ ...action.payload.meta, ...state.meta[endpoint].data ];

      // Append total number of attachments
      if (state.meta[endpoint].meta.pagination === undefined) {
        state.meta[endpoint].meta.pagination = { count: 0 };
      }
      state.meta[endpoint].meta.pagination.count = state.meta[endpoint].meta.pagination.count
        + action.payload.attachmentsEntity.length;
    });
  }
});

export const mediaAttachmentsSelectors = mediaAttachmentsAdapter
  .getSelectors((state: RootState) => state.mediaAttachments);
